import { create } from "zustand";
import { desktop } from "@/desktop/bridge";

/**
 * Tamanho do texto: a letra do editor e a do terminal (por pessoa, neste computador) e o zoom da
 * interface inteira (no app desktop, o zoom da janela — o mesmo de Ctrl+= e Ctrl+-).
 */
export const EDITOR_FONT = { min: 10, max: 28, default: 14 } as const;
export const TERMINAL_FONT = { min: 10, max: 24, default: 13 } as const;
export const ZOOM_STEPS = [0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2] as const;

const KEY = "kompil.textSize";

interface Saved {
  editorFont: number;
  terminalFont: number;
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, Math.round(n)));

function read(): Saved {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<Saved> | null;
    return {
      editorFont:
        typeof raw?.editorFont === "number"
          ? clamp(raw.editorFont, EDITOR_FONT.min, EDITOR_FONT.max)
          : EDITOR_FONT.default,
      terminalFont:
        typeof raw?.terminalFont === "number"
          ? clamp(raw.terminalFont, TERMINAL_FONT.min, TERMINAL_FONT.max)
          : TERMINAL_FONT.default,
    };
  } catch {
    return { editorFont: EDITOR_FONT.default, terminalFont: TERMINAL_FONT.default };
  }
}

const hasZoomApi = () => typeof desktop?.zoom?.set === "function";

export const useTextSize = create<
  Saved & {
    /** Zoom da interface (1 = 100%); null fora do app desktop. */
    zoom: number | null;
    setEditorFont(n: number): void;
    setTerminalFont(n: number): void;
    setZoom(factor: number): void;
    reset(): void;
  }
>((set, get) => {
  const save = () => {
    const { editorFont, terminalFont } = get();
    try {
      localStorage.setItem(KEY, JSON.stringify({ editorFont, terminalFont }));
    } catch {
      // sem armazenamento: vale só nesta sessão
    }
  };
  return {
    ...read(),
    zoom: hasZoomApi() ? 1 : null,
    setEditorFont: (n) => {
      set({ editorFont: clamp(n, EDITOR_FONT.min, EDITOR_FONT.max) });
      save();
    },
    setTerminalFont: (n) => {
      set({ terminalFont: clamp(n, TERMINAL_FONT.min, TERMINAL_FONT.max) });
      save();
    },
    setZoom: (factor) => {
      if (!hasZoomApi()) return;
      const f = Math.min(2, Math.max(0.67, factor));
      set({ zoom: f });
      desktop!.zoom.set(f);
    },
    reset: () => {
      set({ editorFont: EDITOR_FONT.default, terminalFont: TERMINAL_FONT.default });
      save();
      get().setZoom(1);
    },
  };
});

/** Próximo degrau de zoom para cima (+1) ou para baixo (-1). */
export function nextZoom(current: number, dir: 1 | -1): number {
  if (dir > 0) return ZOOM_STEPS.find((z) => z > current + 0.001) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1]!;
  return [...ZOOM_STEPS].reverse().find((z) => z < current - 0.001) ?? ZOOM_STEPS[0]!;
}

export const isDefaultTextSize = (s: { editorFont: number; terminalFont: number; zoom: number | null }) =>
  s.editorFont === EDITOR_FONT.default &&
  s.terminalFont === TERMINAL_FONT.default &&
  (s.zoom === null || Math.abs(s.zoom - 1) < 0.01);

// A letra do editor vale para todos os editores abertos (variável de CSS lida pelo tema).
function applyEditorFont(px: number) {
  if (typeof document !== "undefined")
    document.documentElement.style.setProperty("--editor-font-size", `${px}px`);
}
applyEditorFont(useTextSize.getState().editorFont);
useTextSize.subscribe((s, prev) => {
  if (s.editorFont !== prev.editorFont) applyEditorFont(s.editorFont);
});

// O zoom da janela muda também pelos atalhos e pelo menu: acompanha.
if (hasZoomApi()) {
  void desktop!.zoom.get().then((z) => useTextSize.setState({ zoom: z }));
  desktop!.zoom.onChange((z) => useTextSize.setState({ zoom: z }));
}
